import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Grid, Typography } from "@mui/material"
import { DateTime } from "luxon"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { Row } from "@tanstack/react-table"
import { AccountingQuery, FetchAccountingMeetingServiceResponse, fetchAccountingMeetingService } from "../accountingApi"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import OrderStatusBadge from "../../../../../shared/components/badges/OrderStatusBadge"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Receipt } from "../../../../receipt/pages/ReceiptDialog"
import { useExportOrdersToCsv } from "./ExportFunctionMeetingServiceOrder"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../shared/components/table/TableSummaryBox"
import { OrderStatus } from "../../../controlKitchenTypes"
import { MeetingServiceOrder, ReferenceType } from "../accountingTypes"
import {
    defaultMeetingServiceFilterOptionsList,
    MeetingServiceFilterProps,
    FiltersDrawerMeetingService,
} from "../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import useActiveFiltersCount from "../../../../supplier/reporting/accounting/meetingService/hooks/useActiveFiltersCount"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardCustomerSelect } from "../../../../../shared/components/filters/components/StandardCustomerSelect"
import { UserMeetingsReceipt } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { AccountingOrderMenu } from "./AccountingOrderMenu"
import { makeStyles } from "tss-react/mui"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useCreditNoteDialog } from "../../../../../shared/components/dialogs/creditNoteDialog/useCreditNoteDialog"
import { getOrderPerson } from "../../../../../shared/utils/getOrderPerson"

type MeetingServiceAccountingTabProps = Readonly<{}>

export type FilterOrdersProps = Readonly<{
    deliveryDate: {
        startDate: null | DateTime
        endDate: null | DateTime
    }
    orderDetails: {
        customerItem?: {
            customerId: string | undefined
            groupId: string | undefined
        }
        orderNumber: null | number
    }
}>
export const defaultFilterOrdersOptionsList = {
    orderDetails: {
        orderNumber: null,
    },
    deliveryDate: {
        startDate: null,
        endDate: null,
    },
}

const useStyles = makeStyles()(() => ({
    table: {
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

const selectOrders = (data: FetchAccountingMeetingServiceResponse) => data?.orders || []

export function MeetingServiceAccountingTab({}: MeetingServiceAccountingTabProps) {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()

    const [receipt, setReceipt] = useState<Receipt>()
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [globalFilter, setGlobalFilter] = useState<string>("")
    const [showGlobalFilter, setShowGlobalFilter] = useState<boolean>(false)
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<MeetingServiceFilterProps>({
        ...defaultMeetingServiceFilterOptionsList,
        merchantId: merchantId.toString(),
        startBookedDate: startDate,
        endBookedDate: endDate,
    })

    const { activeCount } = useActiveFiltersCount(filtersList)

    const meetingServiceOrdersResult = useApiQuery(fetchAccountingMeetingService, {
        queryName: AccountingQuery.ACCOUNTING_ORDERS,
        dependencies: {
            ...filtersList,
            merchantId,
            startBookedDate: filtersList.startBookedDate?.toISODate(),
            endBookedDate: filtersList.endBookedDate?.toISODate(),
            startDeliveryDate: filtersList.startDeliveryDate?.toISODate(),
            endDeliveryDate: filtersList.endDeliveryDate?.toISODate(),
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        },
        select: selectOrders,
    })
    const { creditNoteDialog, openCreditNoteDialog } = useCreditNoteDialog()

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceOrdersResult)

    const globalFilterFn = (row: Row<MeetingServiceOrder>, _columnId: string, filterValue: string) => {
        if (!filterValue) return true
        const search = filterValue.toLowerCase()

        const { responsible } = getOrderPerson(row.original?.organizers)
        const { id, meetingDetails, deliveryDetails } = row.original

        const meetingName = meetingDetails?.meetingName?.toLowerCase() || ""
        const location = deliveryDetails?.location?.name?.toLowerCase() || ""
        const idStr = String(id).toLowerCase()

        const match =
            idStr.includes(search) ||
            meetingName.includes(search) ||
            responsible?.name?.toLowerCase()?.includes(search) ||
            location.includes(search)

        return match
    }

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-number"),
                accessorKey: "id",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("accounting-orders-meeting-service-table-booked-date"),
                    accessorKey: "accountingDetails.bookedTime",
                },
                (row) => row.accountingDetails.bookedTime,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("accounting-orders-meeting-service-table-delivery-date"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-creator"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <>
                                <Typography variant="body2" component="span" textTransform="uppercase">
                                    {row.original.customer.initials}
                                    {", "}
                                </Typography>
                                <Typography variant="body2" component="span">
                                    {row.original.customer.displayName}
                                </Typography>
                            </>
                        }
                        subtitle1={row.original.meetingDetails.meetingName}
                    />
                ),
            }),
            columnFactory.idColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-ref"),
                accessorFn: (row) => {
                    const value = row.accountingDetails?.references?.find(
                        (r) => r.referenceType === ReferenceType.ORIGINAL_ORDER,
                    )

                    return value?.orderId
                },
            }),
            columnFactory.statusColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-status"),
                accessorKey: "orderStatus",
                Cell: ({ row }) => {
                    return <OrderStatusBadge placement="table" status={row.original.orderStatus as OrderStatus | ""} />
                },
                filterSelectOptions: [
                    {
                        value: "CONFIRMED",
                        text: l10n.getString("production-order-status-confirmed"),
                    },
                    {
                        value: "RECEIVED",
                        text: l10n.getString("production-order-status-received"),
                    },
                    {
                        value: "CANCELLED",
                        text: l10n.getString("production-order-status-cancelled"),
                    },
                    {
                        value: "READY",
                        text: l10n.getString("production-order-status-ready"),
                    },
                ],
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("accounting-orders-meeting-service-table-amount"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    function showReceipt(row: MeetingServiceOrder) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const exportOrder = useExportOrdersToCsv({
        data: meetingServiceOrdersResult.data,
        startDate: filtersList.startBookedDate || DateTime.now(),
        endDate: filtersList.endBookedDate || DateTime.now(),
    })

    const handleClearSelected = useCallback(() => {
        setSelectedRows(undefined)
        setRowSelection({})
    }, [])

    return (
        <>
            <Grid container direction="column" wrap="nowrap" className={classes.table}>
                <ApiQueryTable
                    filterFns={{
                        myCustomFilterFn: globalFilterFn,
                    }}
                    globalFilterFn="myCustomFilterFn"
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <AccountingOrderMenu
                            setShowReceiptModal={showReceipt}
                            openCreditNoteDialog={openCreditNoteDialog}
                            order={row.original}
                            handleMenuClose={closeMenu}
                        />,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                            <StandardExportButton
                                onClick={() => {
                                    exportOrder()
                                }}
                            />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter
                                value={{
                                    startDate: filtersList.startBookedDate,
                                    endDate: filtersList.endBookedDate,
                                }}
                                onChange={(newValue) => {
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        startBookedDate: newValue.startDate,
                                        endBookedDate: newValue.endDate,
                                    }))
                                }}
                            />
                            <StandardCustomerSelect
                                setCustomerId={(customerId) =>
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        customerId,
                                    }))
                                }
                                setCustomerGroupId={(userGroupId) =>
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        userGroupId,
                                    }))
                                }
                                customerId={filtersList.customerId}
                                customerGroupId={filtersList.userGroupId}
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                    state={{
                        rowSelection,
                        globalFilter,
                        showGlobalFilter,
                    }}
                    onGlobalFilterChange={setGlobalFilter}
                    onShowGlobalFilterChange={setShowGlobalFilter}
                />
            </Grid>
            {/* {selectedRows && (
                <FloatingOrderActionBar
                    selectedRows={selectedRows}
                    startDate={startDate}
                    endDate={endDate}
                    handleClearSelected={handleClearSelected}
                />
            )} */}

            {receipt && (
                <UserMeetingsReceipt
                    open={Boolean(receipt)}
                    onClose={() => setReceipt(undefined)}
                    id={receipt.orderId}
                    meetings={meetingServiceOrdersResult.data}
                />
            )}
            {creditNoteDialog}

            <FiltersDrawerMeetingService
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                tab="accounting-meeting-service"
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
                disabledFilters={{
                    disableMerchantShops: true,
                    disableServicePayment: true,
                }}
            />
        </>
    )
}
